
import { Companies, Events, Quotes } from "@/api";
import { QuoteCompany, QuoteCompanyCourse, QuotePrivate } from "@/api/types";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

interface QuoteInfo {
  id: number;
  desiredDate: string;
  companyName: string;
  companyType: number;
  companyId: string;
  clientName: string;
  ccAgent: string;
  statusName: string;
  totalPrice: number;
  premiseTypeId: number;
  travelCost: number;
  accomodationCost: number;
  instructorFee: number;
  lunchFee: number;
  courierFee: number;
  carHireFee: number;
  courses: {
    id: number;
    lineType: string;
    qtyDelegates: number;
    name: string;
    discountedPrice: number;
    newPrice: number;
    price: number;
    premiseTypeId: number;
    travelCost: number;
    accommodationCost: number;
    comment: string | null;
  }[];
  /** Legacy: quotes raised before comments moved onto each course line. */
  comments: string;
}

/** office_types.id of the online premise. */
const ONLINE_PREMISE = 3;

@Component({
  components: { ValidationObserver, ValidationProvider }
})
export default class QuoteFormModal extends Vue {
  @Prop()
  quote!: any;

  /** LMS-1224: existing customers offered as suggestions when creating. */
  @Prop({ default: () => [] })
  companies!: Array<any>;

  quoteInfo: QuoteInfo = {} as QuoteInfo;
  isPrivate = false;

  courses: any[] = [];
  officeTypes: any = [];
  errors = [];

  initialCourse = {
    courseKey: undefined,
    qtyDelegates: undefined,
    discountedPrice: undefined,
    coursePrice: undefined,
    premiseTypeId: 2,
    travelCost: undefined,
    accommodationCost: undefined,
    comment: ""
  };

  quotePrivate: QuotePrivate = {
    fullName: "",
    personId: "",
    companyId: undefined,
    typeId: 1,
    courses: [{ ...this.initialCourse }]
  };

  quoteCompany: QuoteCompany = {
    companyName: "",
    address: "",
    courses: [{ ...this.initialCourse }],
    coursePrice: undefined,
    discountedPrice: undefined,
    premiseTypeId: 2,
    travelCost: undefined,
    accomodationCost: undefined,
    instructorFee: undefined,
    lunchFee: undefined,
    courierFee: undefined,
    carHireFee: undefined,
    companyId: undefined,
    typeId: 2
  };

  async created() {
    try {
      const [lineItems, officeTypes] = await Promise.all([
        Quotes.lineItems(),
        Events.officeTypes()
      ]);
      this.courses = lineItems.map((item: any) => ({
        ...item,
        // A course and an online course can share a name to the letter
        // ("Convey of Dangerous Goods by Road" is both), and a quote raised
        // against the wrong one cannot be booked against. The kind is part of
        // the name here so it is searchable and stays visible once chosen.
        selectLabel: item.lineTypeName
          ? `${item.name} - ${item.lineTypeName}`
          : item.name
      }));
      this.officeTypes = officeTypes;

      if (!this.isCreate) {
        this.fetchQuote();
      }
      // this.fetchCompanyInfo(this.companyId);
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }

  /** LMS-1224 */
  get isCreate(): boolean {
    return !this.quote;
  }

  get companyNames(): Array<string> {
    return (this.companies || []).map((company: any) => company.name);
  }

  /**
   * The typed name decides everything: a name already in the system means the
   * quote goes to that customer, anything else creates a prospective one.
   */
  get matchedCompany(): any {
    const name = (this.quoteCompany.companyName || "").trim().toLowerCase();

    if (!name) {
      return null;
    }

    return (
      (this.companies || []).find(
        (company: any) => (company.name || "").trim().toLowerCase() === name
      ) || null
    );
  }

  get isExistingCustomer(): boolean {
    return Boolean(this.matchedCompany);
  }

  get customerType(): string {
    return this.isExistingCustomer ? "EXISTING" : "NEW";
  }

  /**
   * Recognised name — pull the customer's data in and lock the extra fields;
   * unknown name — start with a clean slate for the new customer.
   */
  async onCustomerNameChange() {
    const matched = this.matchedCompany;

    if (!matched) {
      this.quoteCompany.companyId = undefined;
      return;
    }

    this.quoteCompany.companyId = matched.id;

    try {
      const info = await Companies.info(String(matched.id));
      this.quoteCompany.address = info.address;
      this.quoteCompany.regNumber = info.regNumber;
      this.quoteCompany.vat = info.vat;
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }

  @Watch("quote.id", { deep: true })
  fetchQuoteHandler() {
    if (!this.isCreate) {
      this.fetchQuote();
    }
  }

  async fetchQuote() {
    this.quoteInfo = await Quotes.fetchQuoteById(this.quote.id);

    this.isPrivate = +this.quoteInfo?.companyType == 2;

    this.fetchCompanyInfo(this.quoteInfo.companyId);

    if (this.isPrivate) {
      this.quotePrivate.courses = this.quoteInfo.courses.map(course => ({
        courseKey: `${course.lineType}:${course.id}`,
        qtyDelegates: course.qtyDelegates,
        discountedPrice:
          +course.newPrice !== +course.price ? course.newPrice : undefined,
        coursePrice: course.price,
        comment: course.comment || ""
      }));
    } else {
      // this.quoteCompany = { ...this.quoteInfo } as any;
      this.quoteCompany.courses = this.quoteInfo.courses.map(course => ({
        courseKey: `${course.lineType}:${course.id}`,
        qtyDelegates: course.qtyDelegates,
        discountedPrice:
          +course.newPrice !== +course.price ? course.newPrice : undefined,
        coursePrice: course.price,
        premiseTypeId: course.premiseTypeId || 2,
        travelCost: course.travelCost,
        accommodationCost: course.accommodationCost,
        comment: course.comment || ""
      }));
      this.quoteCompany.instructorFee = this.quoteInfo.instructorFee;
      this.quoteCompany.premiseTypeId = this.quoteInfo.premiseTypeId;
      this.quoteCompany.travelCost = this.quoteInfo.travelCost;
      this.quoteCompany.accomodationCost = this.quoteInfo.accomodationCost;
      this.quoteCompany.lunchFee = this.quoteInfo.lunchFee;
      this.quoteCompany.courierFee = this.quoteInfo.courierFee;
      this.quoteCompany.carHireFee = this.quoteInfo.carHireFee;
    }
  }

  async fetchCompanyInfo(id: string) {
    if (!this.isPrivate) {
      const companyInfo = await Companies.info(id);
      this.quoteCompany.companyName = companyInfo.name;
      this.quoteCompany.address = companyInfo.address;
      this.quoteCompany.companyId = id;
    } else {
      const companyInfoPrivate = await Companies.infoPrivate(id);
      this.quotePrivate.fullName = companyInfoPrivate.name;
      this.quotePrivate.personId = companyInfoPrivate.passport;
      this.quotePrivate.companyId = id;
    }
  }

  /**
   * The dropdown already carries every line item's price, so a selection does
   * not have to be looked up per kind — which it could not be anyway, since a
   * CC agent need not have permission to read an audit.
   */
  setCoursePrice(course: QuoteCompanyCourse) {
    const selected = this.courses.find(
      (item: any) => item.key === course.courseKey
    );
    course.coursePrice = selected ? selected.price : undefined;
  }

  onCoursePremiseChange(course: QuoteCompanyCourse) {
    if (course.premiseTypeId !== 2) {
      course.travelCost = 0;
      course.accommodationCost = 0;
    }
  }

  /**
   * Where a line can be delivered: an online course only online, a training
   * course or an audit at our office, the customer's premise or online.
   */
  premiseOptionsFor(course: QuoteCompanyCourse): any[] {
    if (this.isOnlineLine(course)) {
      return this.officeTypes.filter((type: any) => type.id === ONLINE_PREMISE);
    }
    return this.officeTypes;
  }

  isOnlineLine(course: QuoteCompanyCourse): boolean {
    return String(course.courseKey || "").startsWith("online_course:");
  }

  /** Picking an online course settles its premise; nothing else fits. */
  onCourseLineChange(course: QuoteCompanyCourse): void {
    if (this.isOnlineLine(course)) {
      course.premiseTypeId = ONLINE_PREMISE;
      this.onCoursePremiseChange(course);
    } else if (course.premiseTypeId === ONLINE_PREMISE) {
      course.premiseTypeId = 2;
    }
  }

  addCourse(company: any) {
    company.courses.push({ ...this.initialCourse });
  }

  removeCourse(
    company: QuotePrivate | QuoteCompany,
    courseToDelete: QuoteCompanyCourse
  ) {
    company.courses = company.courses.filter(
      course => course !== courseToDelete
    );
  }

  async transformEmptyValues() {
    if (this.isPrivate) {
      // this.quotePrivate.courses.forEach(course => {
      //   course.discountedPrice = course.discountedPrice
      //     ? course.discountedPrice
      //     : 0;
      // });
    } else {
      this.quoteCompany.courses.forEach(course => {
        course.travelCost = course.travelCost || 0;
        course.accommodationCost = course.accommodationCost || 0;
      });
      // this.quoteCompany.courses.forEach(course => {
      //   course.discountedPrice = course.discountedPrice
      //     ? course.discountedPrice
      //     : 0;
      //   course.qtyDelegates = course.qtyDelegates ? course.qtyDelegates : 1;
      // });
      this.quoteCompany.travelCost = this.quoteCompany.travelCost
        ? this.quoteCompany.travelCost
        : 0;
      this.quoteCompany.accomodationCost = this.quoteCompany.accomodationCost
        ? this.quoteCompany.accomodationCost
        : 0;
      this.quoteCompany.instructorFee = this.quoteCompany.instructorFee
        ? this.quoteCompany.instructorFee
        : 0;
      this.quoteCompany.lunchFee = this.quoteCompany.lunchFee
        ? this.quoteCompany.lunchFee
        : 0;
      this.quoteCompany.courierFee = this.quoteCompany.courierFee
        ? this.quoteCompany.courierFee
        : 0;
      this.quoteCompany.carHireFee = this.quoteCompany.carHireFee
        ? this.quoteCompany.carHireFee
        : 0;
    }
  }

  async onCloseModal() {
    this.$modal.hide("quote-form-modal");
    this.$emit("close");
    this.isPrivate = false;
    this.quoteCompany = {
      companyName: "",
      address: "",
      courses: [{ ...this.initialCourse }],
      coursePrice: undefined,
      discountedPrice: undefined,
      premiseTypeId: 2,
      travelCost: undefined,
      accomodationCost: undefined,
      instructorFee: undefined,
      lunchFee: undefined,
      courierFee: undefined,
      carHireFee: undefined,
      companyId: undefined,
      typeId: 2
    };
    this.quotePrivate = {
      fullName: "",
      personId: "",
      companyId: undefined,
      typeId: 1,
      courses: [{ ...this.initialCourse }]
    };
  }

  async postCreateQuote() {
    try {
      const valid: any = this.isPrivate
        ? await (this.$refs.privateObserver as Vue & {
            validate: () => boolean;
          }).validate()
        : await (this.$refs.companyObserver as Vue & {
            validate: () => boolean;
          }).validate();

      if (valid) {
        await this.transformEmptyValues();

        const data = this.transformDataToRequestBody();

        if (this.isCreate) {
          await Companies.createQuote(this.withCustomerData(data));
        } else {
          await Companies.updateQuote(data, this.quote.id);
        }

        this.$emit("updated");
        this.onCloseModal();
      }
    } catch (e) {
      await this.$error(await this.readError(e));
    }
  }

  /**
   * LMS-1224: tells the API whether the quote goes to an existing customer or
   * to a brand new one that has to be stored as prospective first.
   */
  withCustomerData(data: any): any {
    return {
      ...data,
      customerType: this.customerType,
      customerName: this.quoteCompany.companyName,
      companyId: this.matchedCompany ? this.matchedCompany.id : undefined,
      regNumber: this.quoteCompany.regNumber,
      vat: this.quoteCompany.vat,
      address: this.quoteCompany.address
    };
  }

  /**
   * Quote endpoints answer with a PDF blob on preview and with plain JSON on
   * save, so the error body has to be read differently for each.
   */
  async readError(e: any): Promise<any> {
    const fallback = { err: "Something went wrong" };

    try {
      const data = e?.response?.data;

      if (data && typeof data.text === "function") {
        const parsed = JSON.parse(await data.text());
        return parsed?.result?.message
          ? { err: parsed.result.message }
          : parsed?.errors || fallback;
      }

      if (data?.result?.message) {
        return { err: data.result.message };
      }

      return data?.errors || fallback;
    } catch (parseError) {
      return fallback;
    }
  }

  async postPreviewQuote() {
    try {
      const valid: any = this.isPrivate
        ? await (this.$refs.privateObserver as Vue & {
            validate: () => boolean;
          }).validate()
        : await (this.$refs.companyObserver as Vue & {
            validate: () => boolean;
          }).validate();

      if (valid) {
        await this.transformEmptyValues();

        const data = this.transformDataToRequestBody();

        await Companies.previewQuote(data).then((response: any) => {
          const file = new Blob([response.data], { type: "application/pdf" });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, "_blank");
        });
      }
    } catch (e) {
      const err = e as any;
      const errText = JSON.parse(await err.response.data.text());

      await this.$error(
        (errText?.result?.message && { err: errText.result.message }) || {
          err: "Something went wrong"
        }
      );
    }
  }

  transformDataToRequestBody() {
    const quote: QuoteCompany | QuotePrivate = this.isPrivate
      ? this.quotePrivate
      : this.quoteCompany;

    const courses = [...JSON.parse(JSON.stringify(quote.courses))];

    const data = JSON.parse(JSON.stringify(quote));

    // A quote line is addressed by `<type>:<id>`: a course, an online course
    // and an audit can all share an id, so the id alone would merge them.
    data.courses = courses.reduce((acc, cur) => {
      if (acc[cur.courseKey] && acc[cur.courseKey].length) {
        acc[cur.courseKey].push({
          price: cur.discountedPrice ? cur.discountedPrice : cur.coursePrice,
          qtyDelegates: cur.qtyDelegates,
          premiseTypeId: cur.premiseTypeId,
          travelCost: cur.travelCost || 0,
          accommodationCost: cur.accommodationCost || 0,
          comment: cur.comment || null
        });
      } else {
        acc[cur.courseKey] = [
          {
            price: cur.discountedPrice ? cur.discountedPrice : cur.coursePrice,
            qtyDelegates: cur.qtyDelegates,
            premiseTypeId: cur.premiseTypeId,
            travelCost: cur.travelCost || 0,
            accommodationCost: cur.accommodationCost || 0,
            comment: cur.comment || null
          }
        ];
      }

      return acc;
    }, {});
    return data;
  }
}
